import React, { useState, useEffect } from "react";
import { Modal, Table } from "react-bootstrap";
import { TimePicker, Checkbox } from "antd";
import moment from "moment";
import _ from "lodash";
import {
    getEmployeeList,
    updateBulkReportingTime,
} from "../../../config/services/employeeServices";
import ResponseFilter from "../../../ResponseFilter";
import { Loader } from "../../svg";

const format = "HH:mm";

// Convert seconds to time display (e.g., 34200 -> "09:30")
const formatArrivalTime = (seconds) => {
    // Convert string to number if needed
    const numSeconds = typeof seconds === 'string' ? parseInt(seconds, 10) : seconds;
    if (!numSeconds && numSeconds !== 0) return "-";
    const hours = Math.floor(numSeconds / 3600);
    const minutes = Math.floor((numSeconds % 3600) / 60);
    return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
};

// Convert time string to seconds (e.g., "09:30" -> 34200)
const timeStringToSeconds = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 3600 + minutes * 60;
};

// Calculate working hours from check-in to check-out
const calculateWorkingHours = (arrivalTime, reportOutTime) => {
    // Convert string to number if needed
    const arrivalSeconds = typeof arrivalTime === 'string' ? parseInt(arrivalTime, 10) : arrivalTime;
    const reportOutSeconds = typeof reportOutTime === 'string' ? parseInt(reportOutTime, 10) : reportOutTime;

    if (!arrivalSeconds && arrivalSeconds !== 0) return "-";
    if (!reportOutSeconds && reportOutSeconds !== 0) return "-";

    const diffSeconds = reportOutSeconds - arrivalSeconds;
    if (diffSeconds < 0) return "-"; // Invalid: check-out before check-in

    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
};

export function ReportingTimeModal({ show, handleClose }) {
    const [employees, setEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [bulkArrivalTime, setBulkArrivalTime] = useState(null);
    const [bulkReportOutTime, setBulkReportOutTime] = useState(null);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [editArrivalTime, setEditArrivalTime] = useState(null);
    const [editReportOutTime, setEditReportOutTime] = useState(null);
    const [loader, setLoader] = useState(false);
    const [miniLoader, setMiniLoader] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [sortField, setSortField] = useState("empId");
    const [sortOrder, setSortOrder] = useState("asc");

    useEffect(() => {
        if (show) {
            fetchEmployees();
            setSearchTerm("");
            setSortField("empId");
            setSortOrder("asc");
        }
    }, [show]);

    useEffect(() => {
        let filtered = employees.filter((emp) => {
            const searchLower = searchTerm.toLowerCase();
            const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
            const empId = emp.empId.toLowerCase();
            return fullName.includes(searchLower) || empId.includes(searchLower);
        });

        filtered.sort((a, b) => {
            let compareA, compareB;
            if (sortField === "name") {
                compareA = `${a.firstName} ${a.lastName}`.toLowerCase();
                compareB = `${b.firstName} ${b.lastName}`.toLowerCase();
            } else {
                compareA = a.empId.toLowerCase();
                compareB = b.empId.toLowerCase();
            }
            if (sortOrder === "asc") {
                return compareA.localeCompare(compareB);
            } else {
                return compareB.localeCompare(compareA);
            }
        });

        setFilteredEmployees(filtered);
    }, [employees, searchTerm, sortField, sortOrder]);

    const fetchEmployees = async () => {
        setLoader(true);
        try {
            const res = await getEmployeeList("status=1");
            const { status, resData } = ResponseFilter(res);
            if (status) {
                // Filter active employees and sort by name
                const activeEmployees = (resData.employeesList || [])
                    .filter((emp) => emp.status === 1)
                    .sort((a, b) => a.empId.localeCompare(b.empId));
                setEmployees(activeEmployees);
            }
        } catch (error) {
            console.error("Error fetching employees:", error);
            setErrorMessage("Failed to fetch employees");
        } finally {
            setLoader(false);
        }
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            // Add current filtered employees to selection
            const filteredIds = filteredEmployees.map((emp) => emp.employeeId);
            setSelectedEmployees(_.union(selectedEmployees, filteredIds));
        } else {
            // Remove ONLY current filtered employees from selection
            const filteredIds = filteredEmployees.map((emp) => emp.employeeId);
            setSelectedEmployees(selectedEmployees.filter(id => !filteredIds.includes(id)));
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const handleSelectEmployee = (employeeId, checked) => {
        if (checked) {
            setSelectedEmployees([...selectedEmployees, employeeId]);
        } else {
            setSelectedEmployees(selectedEmployees.filter((id) => id !== employeeId));
        }
    };

    const handleBulkArrivalTimeChange = (time, timeString) => {
        setBulkArrivalTime(timeString);
    };

    const handleBulkReportOutTimeChange = (time, timeString) => {
        setBulkReportOutTime(timeString);
    };

    const handleApplyBulkTime = async () => {
        if ((!bulkArrivalTime && !bulkReportOutTime) || selectedEmployees.length === 0) {
            setErrorMessage("Please select employees and at least one time");
            setTimeout(() => setErrorMessage(""), 3000);
            return;
        }

        setMiniLoader(true);
        try {
            const payload = {
                employeeIds: selectedEmployees,
            };
            if (bulkArrivalTime) {
                payload.arrivalTime = timeStringToSeconds(bulkArrivalTime);
            }
            if (bulkReportOutTime) {
                payload.reportOutTime = timeStringToSeconds(bulkReportOutTime);
            }

            const res = await updateBulkReportingTime(payload);

            if (res.data.statusCode === 1) {
                setSuccessMessage(
                    `Updated reporting time for ${selectedEmployees.length} employees`
                );
                setTimeout(() => setSuccessMessage(""), 3000);
                // Refresh the list
                await fetchEmployees();
                setSelectedEmployees([]);
                setBulkArrivalTime(null);
                setBulkReportOutTime(null);
            } else {
                setErrorMessage(res.data.error?.responseMessage || "Update failed");
                setTimeout(() => setErrorMessage(""), 3000);
            }
        } catch (error) {
            console.error("Error updating reporting time:", error);
            setErrorMessage("Failed to update reporting time");
            setTimeout(() => setErrorMessage(""), 3000);
        } finally {
            setMiniLoader(false);
        }
    };

    const handleEditClick = (employee) => {
        setEditingEmployee(employee.employeeId);
        setEditArrivalTime(
            employee.arrivalTime
                ? moment
                    .utc(employee.arrivalTime * 1000)
                    .format(format)
                : null
        );
        setEditReportOutTime(
            employee.reportOutTime
                ? moment
                    .utc(employee.reportOutTime * 1000)
                    .format(format)
                : null
        );
    };

    const handleEditArrivalTimeChange = (time, timeString) => {
        setEditArrivalTime(timeString);
    };

    const handleEditReportOutTimeChange = (time, timeString) => {
        setEditReportOutTime(timeString);
    };

    const handleSaveEdit = async (employeeId) => {
        if (!editArrivalTime && !editReportOutTime) {
            setErrorMessage("Please select at least one time");
            setTimeout(() => setErrorMessage(""), 3000);
            return;
        }

        setMiniLoader(true);
        try {
            const payload = {
                employeeIds: [employeeId],
            };
            if (editArrivalTime) {
                payload.arrivalTime = timeStringToSeconds(editArrivalTime);
            }
            if (editReportOutTime) {
                payload.reportOutTime = timeStringToSeconds(editReportOutTime);
            }

            const res = await updateBulkReportingTime(payload);

            if (res.data.statusCode === 1) {
                setSuccessMessage("Time updated successfully");
                setTimeout(() => setSuccessMessage(""), 3000);
                setEditingEmployee(null);
                setEditArrivalTime(null);
                setEditReportOutTime(null);
                // Refresh the list
                await fetchEmployees();
            } else {
                setErrorMessage(res.data.error?.responseMessage || "Update failed");
                setTimeout(() => setErrorMessage(""), 3000);
            }
        } catch (error) {
            console.error("Error updating reporting time:", error);
            setErrorMessage("Failed to update reporting time");
            setTimeout(() => setErrorMessage(""), 3000);
        } finally {
            setMiniLoader(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingEmployee(null);
        setEditArrivalTime(null);
        setEditReportOutTime(null);
    };

    const handleModalClose = () => {
        setSelectedEmployees([]);
        setBulkArrivalTime(null);
        setBulkReportOutTime(null);
        setEditingEmployee(null);
        setEditArrivalTime(null);
        setEditReportOutTime(null);
        setSuccessMessage("");
        setErrorMessage("");
        handleClose();
    };

    const isAllSelected =
        filteredEmployees.length > 0 &&
        filteredEmployees.every((emp) => selectedEmployees.includes(emp.employeeId));

    return (
            <Modal show={show} onHide={handleModalClose} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <b>MANAGE REPORTING TIME</b>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loader && <div className="cover-spin1"></div>}

                    {/* Success/Error Messages */}
                    {successMessage && (
                        <div className="alert alert-success mb-3">{successMessage}</div>
                    )}
                    {errorMessage && (
                        <div className="alert alert-danger mb-3">{errorMessage}</div>
                    )}

                    {!loader && (
                        <>
                            {/* Actions & Search Row */}
                            <div className="row mb-4 align-items-end">
                                {selectedEmployees.length > 0 && (
                                    <>
                                        <div className="col-auto">
                                            <label className="cm_bold mb-1">Set Bulk Arrival Time:</label>
                                                <input
                                                    type="time"
                                            className="form-control"
                                                    value={bulkArrivalTime || ''}
                                                    onChange={(e) => setBulkArrivalTime(e.target.value)}
                                            placeholder="Select Arrival Time"
                                                    disabled={miniLoader}
                                                    style={{
                                                        height: '38px',
                                                width: '180px',
                                                        borderRadius: '4px',
                                                    }}
                                                />
                                        </div>
                                        <div className="col-auto">
                                            <label className="cm_bold mb-1">Set Bulk Report Out Time:</label>
                                                <input
                                                    type="time"
                                            className="form-control"
                                                    value={bulkReportOutTime || ''}
                                                    onChange={(e) => setBulkReportOutTime(e.target.value)}
                                            placeholder="Select Report Out Time"
                                                    disabled={miniLoader}
                                                    style={{
                                                        height: '38px',
                                                width: '180px',
                                                        borderRadius: '4px',
                                                    }}
                                                />
                                        </div>
                                        <div className="col-auto">
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleApplyBulkTime}
                                                disabled={
                                                    miniLoader || (!bulkArrivalTime && !bulkReportOutTime) || selectedEmployees.length === 0
                                                }
                                                style={{ height: '38px', borderRadius: '4px', whiteSpace: 'nowrap', padding: '0 20px' }}
                                            >
                                                Apply ({selectedEmployees.length})
                                            </button>
                                        </div>
                                    </>
                                )}
                                <div className="col">
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search by Name or Employee ID"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{ height: '38px', borderRadius: '4px 0 0 4px' }}
                                        />
                                        <div className="input-group-append">
                                            <span className="input-group-text" style={{ height: '38px', display: 'flex', alignItems: 'center', borderRadius: '0 4px 4px 0', borderLeft: '0' }}>
                                                <i className="fa fa-search"></i>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Employee Table */}
                            <div className="table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
                                <Table bordered hover>
                                    <thead className="bg_blue white" style={{ position: "sticky", top: 0, zIndex: 10 }}>
                                        <tr>
                                            <th style={{ width: "50px" }}>
                                                <Checkbox
                                                    checked={isAllSelected}
                                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                                />
                                            </th>
                                            <th style={{ width: "60px" }}>S.No</th>
                                            <th
                                                style={{ cursor: "pointer" }}
                                                onClick={() => handleSort("empId")}
                                            >
                                                Employee ID{" "}
                                                <i className={`fa ${sortField === "empId" ? `fa-sort-numeric-${sortOrder === "asc" ? "asc" : "desc"}` : "fa-sort"} ml-2`} style={{ opacity: sortField === "empId" ? 1 : 0.4 }}></i>
                                            </th>
                                            <th
                                                style={{ cursor: "pointer" }}
                                                onClick={() => handleSort("name")}
                                            >
                                                Name{" "}
                                                <i className={`fa ${sortField === "name" ? `fa-sort-alpha-${sortOrder === "asc" ? "asc" : "desc"}` : "fa-sort"} ml-2`} style={{ opacity: sortField === "name" ? 1 : 0.4 }}></i>
                                            </th>
                                            <th>Reporting Time</th>
                                            <th>Report Out Time</th>
                                            <th>Working Hours</th>
                                            <th style={{ width: "120px" }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!_.isEmpty(filteredEmployees) ? (
                                            filteredEmployees.map((employee, index) => (
                                                <tr key={employee.employeeId}>
                                                    <td>
                                                        <Checkbox
                                                            checked={selectedEmployees.includes(employee.employeeId)}
                                                            onChange={(e) =>
                                                                handleSelectEmployee(
                                                                    employee.employeeId,
                                                                    e.target.checked
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                    <td>{index + 1}</td>
                                                    <td>{employee.empId}</td>
                                                    <td>
                                                        {employee.firstName} {employee.lastName}
                                                    </td>
                                                    <td>
                                                        {editingEmployee === employee.employeeId ? (
                                                            <TimePicker
                                                                className="form-control"
                                                                format={format}
                                                                value={editArrivalTime ? moment(editArrivalTime, format) : null}
                                                                onChange={handleEditArrivalTimeChange}
                                                                placeholder="Select Arrival Time"
                                                                disabled={miniLoader}
                                                                style={{ width: "100px" }}
                                                            />
                                                        ) : (
                                                            formatArrivalTime(employee.arrivalTime)
                                                        )}
                                                    </td>
                                                    <td>
                                                        {editingEmployee === employee.employeeId ? (
                                                            <TimePicker
                                                                className="form-control"
                                                                format={format}
                                                                value={editReportOutTime ? moment(editReportOutTime, format) : null}
                                                                onChange={handleEditReportOutTimeChange}
                                                                placeholder="Select Report Out Time"
                                                                disabled={miniLoader}
                                                                style={{ width: "100px" }}
                                                            />
                                                        ) : (
                                                            formatArrivalTime(employee.reportOutTime)
                                                        )}
                                                    </td>
                                                    <td>
                                                        {calculateWorkingHours(employee.arrivalTime, employee.reportOutTime)}
                                                    </td>
                                                    <td>
                                                        {editingEmployee === employee.employeeId ? (
                                                            <>
                                                                <button
                                                                    className="btn btn-success btn-sm mr-2"
                                                                    onClick={() => handleSaveEdit(employee.employeeId)}
                                                                    disabled={miniLoader}
                                                                >
                                                                    <i className="fa fa-check"></i>
                                                                </button>
                                                                <button
                                                                    className="btn btn-secondary btn-sm"
                                                                    onClick={handleCancelEdit}
                                                                    disabled={miniLoader}
                                                                >
                                                                    <i className="fa fa-times"></i>
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                className="btn btn-outline-primary btn-sm"
                                                                onClick={() => handleEditClick(employee)}
                                                                disabled={miniLoader}
                                                            >
                                                                <i className="fa fa-pencil"></i>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="text-center">
                                                    No employees found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    )}

                    {miniLoader && <div className="cover-spin1"></div>}
                </Modal.Body>
                {/* <Modal.Footer>
                <button
                    className="btn btn-secondary"
                    onClick={handleModalClose}
                    disabled={miniLoader}
                >
                    Close
                </button>
            </Modal.Footer> */}
            </Modal>
    );
}

export default ReportingTimeModal;
